/*
 * dgm-facets-render.js — zet de facetten in het skelet en verwisselt het grid.
 *
 * Bewust gescheiden van dgm-facets-ui.js (laden en rekenen) en dgm-facet-engine.js
 * (filteren). Deze module is de enige die de DOM aanraakt, zodat de engine testbaar blijft
 * in Node en een fout hier de rekenkant niet kan raken.
 *
 * FAIL-OPEN: elke faalconditie leidt tot "laat staan wat er staat". De server heeft al een
 * werkende, ongefilterde, gepagineerde collectiepagina gestuurd; wij zijn additief.
 *
 * IDENTIEK AAN EURUS, GEEN EIGEN STIJL.
 * De markup die hier gegenereerd wordt gebruikt Eurus' eigen klassen en Alpine-attributen,
 * overgenomen uit snippets/input-filter.liquid en snippets/facets.liquid. De eis was dat de
 * bezoeker geen verschil ziet met een gewone collectie zoals /collections/vitamine-a.
 *
 * Selectors uit het thema (sections/main-collection-product-grid.liquid):
 *   #items-grid     de container met de productkaarten
 *   #ProductCount   de "X producten"-teller
 * Wijzigt het thema die id's, dan stopt deze laag met werken en blijft de native pagina
 * staan — dat is de bedoeling, maar wel iets om bij een thema-update te controleren.
 */

const SEL = {
  grid: '#items-grid',
  pagination: 'nav.pagination',
  sortSelect: '#FormSelectFilter',
  count: '#ProductCount',
  // De ZICHTBARE teller. #ProductCount staat op deze template op `hidden`; alleen deze
  // staat in beeld. Eerder werd alleen de verborgen variant bijgewerkt, dus bleef er
  // "9057 producten" staan terwijl het grid wél gefilterd was.
  countHeader: '#ProductCount_header',
  root: '[data-dgm-facets]',
  list: '[data-dgm-list]',
  zoek: '[data-dgm-zoek]',
  zoekWis: '[data-dgm-zoek-wis]',
  zoekMelding: '[data-dgm-zoek-melding]',
  listMobile: '[data-dgm-list-mobile]',
  chips: '[data-dgm-chips]',
  open: '[data-dgm-open]',
  openCount: '[data-dgm-open-count]',
  chipsMobile: '[data-dgm-chips-mobile]',
  drawer: '[data-dgm-drawer]',
  close: '[data-dgm-close]',
  countDrawer: '[data-dgm-count-drawer]',
  clear: '[data-dgm-clear]',
};

/* Eurus' klassen, letterlijk overgenomen. DIT IS DE ENIGE PLEK waar ze gedupliceerd staan —
 * bewust één plek, zodat een thema-update op één punt te controleren is.
 *
 * WAAROM DE ACCORDIONS GEEN ALPINE GEBRUIKEN, ook al doet Eurus dat wel.
 * Eerste poging emitte Eurus' `x-data` / `x-show` / `x-cloak` en riep daarna
 * Alpine.initTree() aan. Dat werkte niet: op het moment dat onze index geladen is, is
 * Alpine er soms nog niet — het thema zelf checkt daarom overal `if (window.Alpine)` met
 * een `alpine:initialized`-fallback (zie assets/coupon-code.js:221). Onze guard sloeg dan
 * stil over, de nodes bleven ongeïnitialiseerd, en omdat een dichte groep `x-cloak` had —
 * en theme.css.liquid:5259 zet `[x-cloak]{display:none!important}` — was de hele inhoud
 * onzichtbaar. Klikken deed daardoor niets.
 *
 * Betere afhankelijkheid dan betere timing: de accordions doen we zelf, met Eurus' klassen.
 * Visueel identiek, en het werkt ongeacht wanneer Alpine opstart. De mobiele drawer houdt
 * wél Alpine, want die deelt `show` met Eurus' eigen component — maar die markup staat
 * server-side in het snippet, dus Alpine pikt hem op bij zijn normale init en initTree()
 * is nergens meer nodig. */
const EURUS = {
  group: 'js-filter relative',
  summary:
    'facets__summary w-full relative cursor-pointer flex pt-3 pb-3 border-solid items-center'
    + ' border-b justify-between',
  summaryLabel: 'text-medium text-[rgba(var(--colors-heading))]',
  caret: 'h-2.5 w-2.5 inline-block duration-300',
  body: 'border-b md:border-0 border-solid',
  li: 'list-none mb-3 eurus',
  checkbox:
    'checkbox checkbox-checked h-6 w-6 min-w-[24px] border'
    + ' checked:border-[rgba(var(--colors-text-link))]'
    + ' checked:bg-[rgba(var(--colors-text-link))] relative cursor-pointer mr-2 rounded',
  valueText: 'p-break-words',
  // Chip zoals facets.liquid:160-170. De afronding hangt aan settings.edges_type
   // (square / rounded / rounded_corners) en wordt door de config meegegeven, want JS kan
   // Liquid-instellingen niet lezen.
  /* Tekstkleur staat er EXPLICIET bij, terwijl facets.liquid op overerving leunt.
   * Reden: in onze kolom kwam de chip grauw uit terwijl de klassen identiek waren aan
   * die van het thema. De oorzaak van dat overerven heb ik niet kunnen aanwijzen — geen
   * enkele CSS-regel in theme.css of nf-tailwind.css zet daar een kleur. Expliciet
   * zetten maakt het onafhankelijk van wat er buiten de chip gebeurt, en de waarde is
   * dezelfde die native effectief rendert. */
  chip: 'px-3 py-1.5 cursor-pointer flex items-center bg-[rgba(var(--colors-text),0.05)]'
    + ' text-[rgba(var(--colors-text))]',
  chipClose:
    'md:w-2.5 md:h-2.5 md:min-w-[10px] min-w-[8px] ml-2 rtl:mr-2 rtl:ml-0 w-2 h-2'
    + ' cursor-pointer inline-block',
  // Paginering, variant `circle` — dat is wat de template gebruikt
  // (collection.supplement-collectiepagina.json: paginate_type = circle).
  pagUl: 'flex items-center justify-center lg:justify-start flex-wrap font-medium gap-x-4',
  pagLi: 'my-1 md:w-14 md:h-14 flex items-center justify-center',
  pagLink:
    'md:w-14 md:h-14 disable-effect bg-auto bg-none w-11 h-11'
    + ' bg-[rgba(var(--colors-text),0.2)] rounded-full flex items-center justify-center',
  pagCurrent:
    'button button-action boder-none md:w-14 md:h-14 w-11 h-11 rounded-full flex items-center'
    + ' justify-center',
  pagArrowLi: 'rotate-180',
  pagArrow: 'md:p-4 p-3.5 block disable-effect',
};

// Caret-icoon zoals Eurus' icon-alls het rendert. Inline omdat we geen Liquid hebben in JS.
const CARET_SVG =
  '<svg width="100%" height="100%" viewBox="0 0 9 7" fill="none"'
  + ' xmlns="http://www.w3.org/2000/svg" style="fill: none;" aria-hidden="true">'
  + '<path d="M0.75 1.625L4.5 5.375L8.25 1.625" stroke="currentcolor" stroke-width="1"'
  + ' stroke-linecap="round" stroke-linejoin="round"/></svg>';

/* Sluitkruisje zoals Eurus' icon-alls het rendert (icon-close). Stond hier eerder als het
 * tekstteken "×", en dat viel in een doosje van 8 bij 8 pixels uit als een vaag streepje in
 * plaats van een kruisje. Het thema zet daar een SVG die met de doos meeschaalt. */
const CLOSE_SVG =
  '<svg width="100%" height="100%" viewBox="0 0 10 10" fill="none"'
  + ' xmlns="http://www.w3.org/2000/svg" aria-hidden="true">'
  + '<path d="M1 9L5 5M5 5L9 1M5 5L1 1M5 5L9 9" stroke="currentColor" stroke-width="1"'
  + ' stroke-linecap="round" stroke-linejoin="round"/></svg>';

// Hoeveel waarden een facet direct laat zien; de rest komt achter "toon alle N".
// Niet Shopify's 100 — onze index kent die limiet niet, dus dit is een UI-keuze.
const INITIAL_VALUES = 10;
const SEARCH_THRESHOLD = 12;
const DEBOUNCE_MS = 120;

const esc = (s) => String(s).replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

/** Diakriet-ongevoelig normaliseren: de waardenlijst bevat 'Cafeïne' en iemand typt
 *  "cafeine". Zonder dit vindt het zoekveld dat niet. */
const norm = (s) => String(s).toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

/** Zet één facetwaarde aan of uit in de filterstaat.
 *
 * Waarom dit een eigen functie is: `availability` is een BOOLEAN en de andere facetten zijn
 * arrays. Die twee door elkaar halen heeft op 2026-08-20 twee keer een TypeError gegeven —
 * `new Set(true)` bij het aanvinken en `true.filter(...)` bij het weghalen van de chip —
 * waardoor het voorraadfilter niet aan of uit te zetten was. Eén plek die het verschil kent
 * is beter dan twee handlers die het allebei moeten onthouden.
 *
 * Muteert `filters` en geeft hem terug.
 */
export function zetFacetWaarde(filters, key, waarde, aan) {
  if (key === 'availability') {
    /* Voorraad is sinds 2026-08-20 een normaal lijstfacet met de waarden '1' en '0', dus
     * hier hoeft niets bijzonders meer te gebeuren behalve dat de lijst LEEG mag blijven.
     * Leeg betekent "toon alles", en urlFromState houdt dat vast met dgm.voorraad=alles;
     * zonder dat zou de standaard bij de volgende klik weer aanslaan. */
    const huidig = new Set(Array.isArray(filters.availability)
      ? filters.availability.filter((v) => v === '1' || v === '0') : []);
    if (aan) huidig.add(waarde); else huidig.delete(waarde);
    filters.availability = [...huidig];
    return filters;
  }
  if (key === 'price') {
    if (!aan) { delete filters.price_gte; delete filters.price_lte; }
    return filters;
  }
  const huidig = new Set(Array.isArray(filters[key]) ? filters[key] : []);
  if (aan) huidig.add(waarde); else huidig.delete(waarde);
  const lijst = [...huidig];
  if (lijst.length) filters[key] = lijst; else delete filters[key];
  return filters;
}

/** Narrowt deze staat de resultaten echt?
 *
 *  Dit bepaalt of de eerste pageload het servergrid mag laten staan. Staat het fout, dan
 *  toont de kolom "Op voorraad" als aangevinkt terwijl het grid de ongefilterde lijst van
 *  de server blijft tonen — uitverkochte producten incluis.
 *
 *  Twee valkuilen, beide een keer misgegaan:
 *    - `availability` was eerst een boolean en is sinds 2026-08-20 een lijst met '1' en '0'.
 *      Een toets op `=== true` is daarna altijd false, en dan filtert de eerste pageload niet.
 *    - een LEGE lijst is geen filter: leeg betekent "toon ook uitverkocht". Alleen een
 *      gevulde lijst narrowt.
 *
 *  Staat als losse functie en niet als klassemethode omdat hij zonder DOM te testen moet
 *  zijn; de vorige versie stond vals groen omdat geen enkele test hem kon aanroepen. */
export function heeftActieveFilters(filters) {
  const f = filters || {};
  if (f.price_gte != null || f.price_lte != null) return true;
  for (const [key, waarde] of Object.entries(f)) {
    if (key === 'price_gte' || key === 'price_lte') continue;
    if (waarde === true) return true;
    if (Array.isArray(waarde) && waarde.length) return true;
    /* De zoekterm is een STRING, geen array of boolean. Zonder deze regel telde hij niet
       mee: "Wis alles" bleef verborgen en een gedeelde link met ?dgm.zoek= liet het
       ongefilterde servergrid staan, want mount() verwisselt alleen als er een actief
       filter is. */
    if (typeof waarde === 'string' && waarde.trim()) return true;
  }
  return false;
}

export class FacetsRenderer {
  constructor(api) {
    this.api = api;
    this.root = document.querySelector(SEL.root);
    this.grid = document.querySelector(SEL.grid);
    this.state = api.filtersFromURL();
    this.openGroups = new Set();    // welke facetten open staan
    this.showAll = new Set();
    this.searchTerms = new Map();
    this.isMobile = () => window.matchMedia('(max-width: 767px)').matches;
    this.aliases = this._buildAliases();
  }

  _buildAliases() {
    const m = new Map();
    for (const f of this.api.facetConfig.facets || []) {
      for (const [alias, target] of Object.entries(f.aliases || {})) {
        m.set(norm(alias), norm(target));
      }
    }
    return m;
  }

  mount() {
    if (!this.root || !this.grid) {
      console.warn('[dgm-facets] grid of kolom niet gevonden — native pagina blijft staan');
      return false;
    }
    this._wireChrome();
    this._wireSort();
    this._wireZoek();
    this.render();

    /* Grid meteen verversen als er al gefilterd wordt bij het laden.
     *
     * Deze laag is fail-open: de server heeft al een werkende, ongefilterde pagina gestuurd
     * en wij zijn additief. Daarom werd het grid vroeger alleen na een klik verwisseld, en dat
     * was juist zolang de bezoeker zelf moest filteren.
     *
     * Sinds het voorraadfilter standaard aanstaat klopt die aanname niet meer. De kolom zei
     * "Op voorraad" en er stond een chip, terwijl het grid de ongefilterde 9.057 producten van
     * de server bleef tonen, uitverkochte artikelen incluis. Op deze collectie kan de server
     * dat ook niet zelf oplossen: boven 5.000 producten is `collection.filters` leeg, dus
     * Shopify negeert `filter.v.availability` en filtert niets.
     *
     * Alleen verversen als er echt iets te filteren valt, anders kost elke eerste pageload een
     * onnodige kaartophaal. */
    if (this._heeftActieveFilters()) this._swapGrid({ pushState: false });

    window.addEventListener('popstate', () => {
      this.state = this.api.filtersFromURL();
      this.render();
      this._swapGrid({ pushState: false });
    });
    return true;
  }

  /* --- vaste bedieningselementen ---------------------------------------- */

  /* --- zoekveld ---------------------------------------------------------- */

  /** Zoeken binnen de collectie op producthandle.
   *
   * Drie dingen die hier bewust zo staan:
   *
   * 1. Ontkoppeld met 250 ms. Elke toetsaanslag opnieuw filteren kan technisch (de engine
   *    doet één lineaire pas), maar elke commit haalt ook de KAARTEN op via
   *    /search?q=…&view=dgm-cards. Zonder ontkoppeling stuurt "magnesium" negen
   *    kaartverzoeken waarvan er acht meteen achterhaald zijn.
   * 2. Het veld staat statisch in de liquid, niet in de JS-render. Zou het meegetekend
   *    worden, dan verliest de bezoeker zijn cursor bij elke aanslag omdat de facetgroepen
   *    hertekenen.
   * 3. Geen Enter-afhandeling nodig: het veld staat in een form met
   *    `onsubmit="return false;"`, dus Enter doet niets en de pagina herlaadt niet.
   */
  /** Het zoekveld naar de bovenbalk verplaatsen.
   *
   * Die balk is `#FacetsWrapperDesktop`, die het thema zélf met Alpine naar `#header-filter`
   * teleporteert. Op desktop staat daar links niets: het enige kind van dat blok is de
   * mobiele filterknop (`md:hidden`). Dat is precies de lege ruimte in de pil.
   *
   * Verplaatsen met gewone JS en niet met een eigen `x-teleport`: twee teleports naar
   * hetzelfde doel landen als broers náást de pil in plaats van erin, en de volgorde
   * waarin Alpine ze afhandelt is niet iets waar ik op wil leunen. Deze variant faalt open
   * — is de balk er niet (mobiel, of een thema-update die het id hernoemt), dan blijft het
   * veld gewoon in de filterkolom staan en werkt alles.
   *
   * Het element wordt VERPLAATST, niet gekopieerd, dus de gebeurtenissen die er al op
   * zitten blijven werken en er is maar één veld in de DOM. */
  _zoekNaarBalk(houder) {
    const balk = document.querySelector('#FacetsWrapperDesktop');
    if (!balk || !houder) return false;
    const doel = balk.firstElementChild || balk;
    houder.classList.add('dgm-zoek--in-balk');
    doel.insertBefore(houder, doel.firstChild);
    return true;
  }

  _wireZoek() {
    const veld = this.root.querySelector(SEL.zoek);
    if (!veld) return;
    const wis = this.root.querySelector(SEL.zoekWis);
    const melding = this.root.querySelector(SEL.zoekMelding);

    // Beginwaarde uit de URL, zodat een gedeelde link met ?dgm.zoek= het veld gevuld toont.
    veld.value = this.state.filters.zoek || '';
    const bijwerkenWisknop = () => {
      if (wis) wis.classList.toggle('hidden', !veld.value);
    };
    bijwerkenWisknop();

    let timer = null;
    const toepassen = () => {
      const term = veld.value.trim();
      const next = this._mutableState();
      if (term) next.filters.zoek = term; else delete next.filters.zoek;
      this._commit(next);
      if (melding) {
        const n = this.lastResult ? this.lastResult.total : null;
        if (term && n === 0) {
          melding.textContent = `Geen producten met "${term}" in deze collectie.`;
          melding.classList.remove('hidden');
        } else if (term && n != null) {
          melding.textContent = `${n} ${n === 1 ? 'product' : 'producten'} met "${term}".`;
          melding.classList.remove('hidden');
        } else {
          melding.classList.add('hidden');
        }
      }
    };

    veld.addEventListener('input', () => {
      bijwerkenWisknop();
      clearTimeout(timer);
      timer = setTimeout(toepassen, 250);
    });
    if (wis) {
      wis.addEventListener('click', () => {
        veld.value = '';
        bijwerkenWisknop();
        clearTimeout(timer);
        toepassen();
        veld.focus();
      });
    }

    /* Pas verplaatsen als alles aangehaakt is. `this.root` is dan niet meer de ouder, dus
       de opruimlus van "Wis alles" zoekt het veld daarna via document. */
    const houder = this.root.querySelector('[data-dgm-zoekhouder]');
    this._zoekVerplaatst = this._zoekNaarBalk(houder);
  }

  _wireChrome() {
    // De drawer wordt door ALPINE bestuurd, niet door ons: openen, sluiten, Escape,
    // click-away, transitions en het blokkeren van de achtergrond komen uit Eurus' eigen
    // `xFiltersAndSortby`-component en `$store.xPopup`. Daarom staat hier geen
    // drawer-logica meer — dat was eigen gedrag dat onvermijdelijk zou afwijken.
    //
    // Gevolg voor de mobiele batching: eerder wachtte het grid op een 'resultaten
    // tonen'-knop. Eurus heeft die knop niet; daar past elke tik direct toe. We volgen dat,
    // want de eis is geen verschil met een reguliere collectie.
    this._toonMobieleTrigger();
    this._wireDrawer();

    for (const clear of this.root.querySelectorAll(SEL.clear)) {
      clear.addEventListener('click', () => {
        // Het zoekveld staat statisch in de liquid, dus `filters` leegmaken haalt de tekst
        // er niet uit. Zonder dit blijft er een term in het veld staan die niets meer doet.
        // Via document en niet via this.root: het zoekveld is naar de bovenbalk verplaatst
        // en hangt dan buiten de filterkolom.
        for (const v of document.querySelectorAll(SEL.zoek)) v.value = '';
        for (const w of document.querySelectorAll(SEL.zoekWis)) w.classList.add('hidden');
        for (const m of document.querySelectorAll(SEL.zoekMelding)) m.classList.add('hidden');
        this._commit({ filters: {}, sortBy: this.state.sortBy, page: 1 });
      });
    }
  }

  /** Alleen zichtbaar maken, NIET verplaatsen.
   *
   *  De vorige versie verplaatste deze knop naar de rij van de sorteerdropdown, omdat dat
   *  de plek is waar Eurus hem zet. Gevolg: die rij is in sommige varianten
   *  `hidden md:flex`, dus de knop werd op mobiel onzichtbaar — precies het symptoom
   *  "geen filter op mobiel".
   *
   *  Niet nodig ook: Eurus' facets-snippet rendert zelf óók binnen de filterkolom
   *  (`col-span-1`), dus deze knop staat hier al op dezelfde plek in de DOM. `md:hidden` op
   *  de knop regelt de rest. */
  _toonMobieleTrigger() {
    const btn = this.root.querySelector(SEL.open);
    if (btn) btn.hidden = false;
  }

  /** Mobiele drawer, eigen mechaniek met Eurus' klassen.
   *
   *  Hing eerst aan Eurus' Alpine-state (`show` + `$store.xPopup`). Dat was dezelfde
   *  afhankelijkheid die op desktop al stil faalde: Alpine is er niet altijd op tijd, en
   *  `x-cloak` maakt de boel dan onzichtbaar zonder melding. Op mobiel leverde dat "geen
   *  filters". Nu deterministisch: hidden-attribuut plus een data-open-vlag waar de CSS de
   *  slide aan hangt. */
  _wireDrawer() {
    const btn = this.root.querySelector(SEL.open);
    const drawer = this.root.querySelector(SEL.drawer);
    if (!btn || !drawer) return;

    // Schakelt `style.display`, NIET het hidden-attribuut. Dat laatste werkte niet: de
    // overlay heeft Tailwind's `flex` in zijn klassenlijst en `display:flex` verslaat de
    // browserregel `[hidden]{display:none}`. Gevolg was een permanent zichtbare overlay
    // waar niet meer uit te komen was. Een inline style is nergens van afhankelijk.
    const zet = (open) => {
      if (open) {
        drawer.style.display = 'flex';
        // Eén frame wachten zodat de CSS-transitie daadwerkelijk loopt; direct na het
        // zichtbaar maken staat het paneel al op zijn eindpositie.
        requestAnimationFrame(() => drawer.setAttribute('data-open', ''));
      } else {
        drawer.removeAttribute('data-open');
        setTimeout(() => { drawer.style.display = 'none'; }, 500);  // = transitieduur
      }
      btn.setAttribute('aria-expanded', String(open));
      document.body.style.overflow = open ? 'hidden' : '';
    };

    btn.addEventListener('click', (e) => { e.preventDefault(); zet(true); });
    for (const c of this.root.querySelectorAll(SEL.close)) {
      c.addEventListener('click', (e) => { e.preventDefault(); zet(false); });
    }
    // Klik op de overlay buiten het paneel sluit, net als Eurus' click.away.
    drawer.addEventListener('click', (e) => { if (e.target === drawer) zet(false); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.style.display !== 'none') zet(false);
    });
  }

  /* --- renderen ---------------------------------------------------------- */

  _heeftActieveFilters() {
    return heeftActieveFilters(this.state.filters);
  }

  render() {
    const effective = this.state;
    const res = this.api.run(effective);
    this.lastResult = res;

    const html = res.facets.map((f) => this._groupHTML(f, effective)).join('');
    for (const sel of [SEL.list, SEL.listMobile]) {
      const target = this.root.querySelector(sel);
      if (!target) continue;
      target.innerHTML = html;
      target.setAttribute('aria-busy', 'false');
      this._wireGroups(target, res);
    }

    this._renderChips(res, effective);
    this._renderCounts(res);
    this._renderPagination(res);
    return res;
  }

  /** Paginering met Eurus' eigen `circle`-markup, maar op ONZE paginatelling.
   *
   *  Nodig omdat Eurus' paginering van Shopify's `paginate`-object komt, en dat weet niets
   *  van onze filtering: het aantal pagina's en de links zouden verwijzen naar een
   *  ongefilterde set. Zonder dit klikt de bezoeker naar pagina 3 van iets anders. */
  _renderPagination(res) {
    const nav = document.querySelector(SEL.pagination);
    if (!nav) return;
    if (res.pages <= 1) { nav.innerHTML = ''; return; }

    // Vensterschuif rond de huidige pagina; Shopify doet hetzelfde met max 5 zichtbaar.
    const rond = 2;
    const van = Math.max(1, res.page - rond);
    const tot = Math.min(res.pages, res.page + rond);
    const items = [];

    if (res.page > 1) {
      items.push(`<li class="${EURUS.pagArrowLi}"><a href="#" class="${EURUS.pagArrow}"`
        + ` data-dgm-page="${res.page - 1}" aria-label="Vorige">←</a></li>`);
    }
    for (let p = van; p <= tot; p++) {
      if (p === res.page) {
        items.push(`<li class="${EURUS.pagLi}"><p aria-disabled="true"`
          + ` class="${EURUS.pagCurrent}" aria-current="page">${p}</p></li>`);
      } else {
        items.push(`<li class="${EURUS.pagLi}"><a href="#" class="${EURUS.pagLink}"`
          + ` data-dgm-page="${p}">${p}</a></li>`);
      }
    }
    if (res.page < res.pages) {
      items.push(`<li class="${EURUS.pagArrowLi}"><a href="#" class="${EURUS.pagArrow}"`
        + ` data-dgm-page="${res.page + 1}" aria-label="Volgende">→</a></li>`);
    }

    nav.innerHTML = `<ul class="${EURUS.pagUl}" role="list">${items.join('')}</ul>`;
    for (const a of nav.querySelectorAll('[data-dgm-page]')) {
      a.addEventListener('click', (e) => {
        e.preventDefault();
        const st = this._mutableState();
        st.page = Number(a.dataset.dgmPage) || 1;
        this.state = st;
        this.render();
        this._swapGrid();
      });
    }
  }

  /** Eurus' sorteerdropdown laten samenwerken met onze engine.
   *
   *  Zonder dit submit die naar Shopify's filter-engine, en die sorteert boven de 5.000 net
   *  zo min als hij filtert. De bezoeker zou dan een dropdown zien die niets doet. */
  _wireSort() {
    const sel = document.querySelector(SEL.sortSelect);
    if (!sel) return;
    sel.value = this.state.sortBy || sel.value;
    sel.addEventListener('change', (e) => {
      e.stopPropagation();          // Eurus' onSubmit() niet laten meelopen
      const st = this._mutableState();
      st.sortBy = sel.value;
      st.page = 1;
      this.state = st;
      this.render();
      this._swapGrid();
    }, true);                       // capture: vóór Eurus' eigen listener
  }

  _renderCounts(res) {
    const drawerCount = this.root.querySelector(SEL.countDrawer);
    if (drawerCount) drawerCount.textContent = `${res.total} producten`;
    /* Beide tellers bijwerken, en met de tekst van het thema.
     *
     * Twee fouten zaten hier. Alleen `#ProductCount` werd bijgewerkt en die staat op deze
     * template op `hidden`; de zichtbare is `#ProductCount_header`. En er werd enkel het
     * getal in geschreven, zonder het woord "producten".
     *
     * Gefilterd gebruikt het thema "X van Y producten" en ongefilterd "Y producten"; zie
     * main-collection-product-grid.liquid r. 236-241. Dat volgen we, zodat de regel
     * hetzelfde leest als op een gewone collectiepagina. */
    const st = this.api.strings || {};
    const basis = this.api.collectionTotal != null ? this.api.collectionTotal : res.total;
    let tekst;
    if (res.total !== basis && st.productCount) {
      tekst = st.productCount.replace('%d', res.total).replace('%t', basis);
    } else if (st.productCountSimple) {
      tekst = st.productCountSimple.replace('%d', res.total);
    } else {
      tekst = String(res.total);
    }
    for (const sel of [SEL.count, SEL.countHeader]) {
      const el = document.querySelector(sel);
      if (el) el.textContent = tekst;
    }
  }

  _renderChips(res, state) {
    const wraps = [this.root.querySelector(SEL.chips),
                   this.root.querySelector(SEL.chipsMobile)].filter(Boolean);
    if (!wraps.length) return;
    const chips = [];
    for (const f of res.facets) {
      for (const v of f.values || []) {
        if (!v.active) continue;
        chips.push(this._chipHTML(f.key, v.value, v.label));
      }
    }
    if (state.filters.price_gte != null || state.filters.price_lte != null) {
      /* Label als bij Eurus: "€0,00 - €41,80", niet het woord "prijs". Zie facets.liquid
       * regel 173-200 — die zet daar de ondergrens (of 0) en de bovengrens (of range_max)
       * neer, gescheiden door een streepje. Ontbreekt de bovengrens in de staat, dan is dat
       * de max van het prijsfacet, precies zoals `filter.range_max` daar. */
      const pf = res.facets.find((x) => x.type === 'price_range');
      const sym = (this.api.money || {}).symbol || '€';
      const lo = (state.filters.price_gte || 0) / 100;
      const hi = (state.filters.price_lte != null ? state.filters.price_lte : (pf && pf.max) || 0) / 100;
      chips.push(this._chipHTML('price', '', `${sym}${this._money(lo)} - ${sym}${this._money(hi)}`));
    }
    for (const wrap of wraps) {
      /* Zelfde omhulling als het thema: <ul class="active-filter"> met <li> per chip. Wij
       * zetten ze eerder los in een div, en dan mist elke CSS-regel die op `.active-filter`
       * of op de lijststructuur mikt. */
      wrap.innerHTML = chips.length
        /* Marges LETTERLIJK uit facets.liquid r. 158: `mt-4 mb-4` plus `md:mb-5` omdat
         * filter_type hier 'aside' is en niet 'above'. Zonder die marges sluit de
         * scheidingslijn van de eerste filtergroep direct op de chips aan. */
        ? `<ul class="active-filter flex flex-wrap gap-2 mt-4 mb-4 md:mb-5" role="list">`
          + chips.map((c) => `<li class="list-none">${c}</li>`).join('') + '</ul>'
        : '';
      wrap.classList.toggle('hidden', chips.length === 0);
      if (chips.length && wrap === wraps[0]) wrap.classList.add('md:flex');
    }
    for (const btn of this.root.querySelectorAll(SEL.clear)) {
      btn.classList.toggle('hidden', chips.length === 0);
    }
    /* "Opnieuw instellen" per facetgroep: haalt alle waarden van dat ene facet weg. Zo
     * gedraagt hij zich als de native knop, die naar `filter.url_to_remove` van dat facet
     * navigeert. */
    for (const btn of this.root.querySelectorAll('[data-dgm-reset-facet]')) {
      btn.addEventListener('click', () => {
        const st = this._mutableState();
        const key = btn.dataset.dgmResetFacet;
        if (key === 'availability') st.filters.availability = [];
        else if (key === 'price') { delete st.filters.price_gte; delete st.filters.price_lte; }
        else delete st.filters[key];
        this._commit(st);
      });
    }
    for (const btn of this.root.querySelectorAll('[data-remove-key]')) {
      btn.addEventListener('click', () => {
        const st = this._mutableState();
        const key = btn.dataset.removeKey;
        zetFacetWaarde(st.filters, key, btn.dataset.removeValue, false);
        this._commit(st);
      });
    }
  }

  /** Chip in Eurus' vorm, inclusief de afronding.
   *
   *  Die komt uit `rounded_filter` (filter_edge_style + buttons_type), niet uit
   *  settings.edges_type — facets.liquid:164 gebruikt exact dezelfde variabele als de duim
   *  van de prijsschuif. Hier stond eerst edges_type, en op deze winkel lopen die twee
   *  uiteen: edges_type is 'rounded_corners' terwijl de filters 'square' zijn. De chips
   *  hadden dus ronde hoeken waar een gewone collectie rechte hoeken laat zien. */
  _chipHTML(key, value, label) {
    const stijl = this.api.filterEdges || this.api.edges;
    const rond = stijl === 'rounded' ? ' rounded-full'
      : stijl === 'rounded_corners' ? ' rounded' : '';
    /* Kleur INLINE, niet via een klasse.
     *
     * De klasse `text-[rgba(var(--colors-text))]` staat in de uitgeleverde CSS en op het
     * element, en DevTools laat zien dat --colors-text daar donkergroen is (#153429). Toch
     * bleef de chiptekst bleek. Wat die klasse overschrijft heb ik niet kunnen vinden: geen
     * regel in theme.css, nf-tailwind.css of een liquid-bestand zet daar kleur of dekking.
     *
     * Een inline stijl wint van elke klasse, dus dit is een pragmatische knoop doorhakken na
     * drie rondes zoeken. Nadeel: het staat niet meer in de themavariabelen te tunen. Vindt
     * iemand later de echte oorzaak, dan kan dit terug naar de klasse. */
    return `<button type="button" style="color: rgb(var(--colors-text))" class="${EURUS.chip}${rond}"`
      + ` data-remove-key="${esc(key)}"${value ? ` data-remove-value="${esc(value)}"` : ''}>`
      + `<span class="active-filter-control">${esc(label)}</span>`
      + `<span class="${EURUS.chipClose}">${CLOSE_SVG}</span></button>`;
  }

  /** Eén facetgroep, met Eurus' klassen maar eigen open/dicht-mechaniek.
   *  Geen x-data/x-show/x-cloak: zie de toelichting bij EURUS. */
  _groupHTML(f, state) {
    if (f.type === 'price_range') return this._priceHTML(f, state);
    const actief = (f.values || []).filter((v) => v.active).length;
    /* Een groep gaat alleen open als de bezoeker hem zelf opent.
     *
     * Hier stond `|| actief > 0`, waardoor de kolom na één klik half uitgeklapt bleef staan
     * en na een deeplink met drie filters vrijwel volledig open. Dat is niet wat we willen,
     * en het verbergt niets: welke waarden actief zijn staat in de chips boven de resultaten
     * en in de teller achter het facetlabel. */
    const open = this.openGroups.has(f.key) || (this.api.groupsStartCollapsed === false && actief > 0);
    return `
      <div class="${EURUS.group}" data-facet="${esc(f.key)}">
        <button type="button" class="${EURUS.summary}" data-dgm-toggle
          aria-expanded="${open}" aria-label="${esc(f.label)}">
          <span class="${EURUS.summaryLabel}">${esc(f.label)}<span class="${actief ? '' : 'hidden'}"> (${actief})</span></span>
          <span class="${EURUS.caret} ${open ? 'rotate-180' : 'rotate-0'}">${CARET_SVG}</span>
        </button>
        <div class="${EURUS.body}" data-dgm-body${open ? '' : ' hidden'}>
          ${open ? this._geselecteerdHTML(f, actief) + this._valuesHTML(f) : ''}
        </div>
      </div>`;
  }

  /** De rij boven de waarden: "N geselecteerd" links, "Opnieuw instellen" rechts.
   *
   *  Letterlijk overgenomen uit snippets/input-filter.liquid r. 94-104, inclusief de
   *  klassen `facets__selected` en `facet-reset` en de berekende tekstgroottes. Die rij
   *  ontbrak in onze kolom, waardoor een open groep zichtbaar afweek van een gewone
   *  collectiepagina. De prijsgroep had hem al wel.
   */
  _geselecteerdHTML(f, actief) {
    const t = this.api.textSizes || {};
    const maat = t.resetDesktop
      ? ` text-[${t.resetMobile}rem] md:text-[${t.resetDesktop}rem]` : '';
    const sjabloon = (this.api.strings && this.api.strings.filtersSelected) || '%d geselecteerd';
    return `
      <div class="flex justify-between py-4${maat}">
        <div><span class="facets__selected">${esc(sjabloon.replace('%d', actief))}</span></div>
        <button type="button" class="facet-reset cursor-pointer ml-5"
          style="color: rgb(var(--colors-text))"
          data-dgm-reset-facet="${esc(f.key)}">${esc(this.api.strings.reset || '')}</button>
      </div>`;
  }

  /** Waardenlijst van één facet. Wordt pas opgebouwd als de groep open staat: de index is al
   *  in de browser, dus dit kost geen netwerk, maar 231 checkboxen maal 7 facetten direct in
   *  de DOM zetten is een meetbare kost. */
  _valuesHTML(f) {
    const term = norm(this.searchTerms.get(f.key) || '');
    const alias = this.aliases.get(term);
    let values = f.values || [];
    if (term) {
      values = values.filter((v) => {
        const n = norm(v.label);
        return n.includes(term) || (alias && n.includes(alias));
      });
    }
    const alles = this.showAll.has(f.key) || !!term;
    const zichtbaar = alles ? values : values.slice(0, INITIAL_VALUES);

    const zoek = (f.totalValues || 0) >= SEARCH_THRESHOLD
      ? `<input type="search" class="dgm-facet-search" inputmode="search"`
        + ` placeholder="Zoek in ${esc(String(f.label).toLowerCase())}…"`
        + ` value="${esc(this.searchTerms.get(f.key) || '')}"`
        + ` data-facet-search="${esc(f.key)}" aria-label="Zoek in ${esc(f.label)}">`
      : '';

    const rows = zichtbaar.map((v, i) => {
      const id = `dgm-${f.key}-${i}`;
      return `<li class="${EURUS.li}">
        <label for="${id}" class="flex items-center${v.count > 0 ? ' cursor-pointer' : ''}">
          <input type="checkbox" id="${id}" class="${EURUS.checkbox}"
            data-facet="${esc(f.key)}" value="${esc(v.value)}"${v.active ? ' checked' : ''}
            ${v.count === 0 && !v.active ? 'disabled' : ''}>
          <span class="${EURUS.valueText}">${esc(v.label)} (${v.count})</span>
        </label>
      </li>`;
    }).join('');

    const meer = !alles && values.length > INITIAL_VALUES
      ? `<button type="button" class="dgm-facet-more" data-facet-more="${esc(f.key)}">`
        + `Toon alle ${values.length}</button>`
      : '';

    const leeg = values.length === 0
      ? '<p class="py-2 opacity-70">Geen waarden die hierop passen.</p>' : '';

    return `<div class="md:pb-5 pb-4 pt-1">${zoek}<ul class="dgm-facet-values" role="list">${rows}</ul>${leeg}${meer}</div>`;
  }

  /** Prijs in het formaat van het thema: bedrag opmaken zoals `money_without_currency`.
   *
   *  Nodig omdat JS die Liquid-filter niet heeft. De scheidingstekens komen uit de config,
   *  afgeleid van de valuta met dezelfde lijst die input-filter.liquid gebruikt — anders
   *  staat er 83.60 onder de schuif waar het thema er 83,60 van maakt.
   *
   *  Let op: Eurus' eigen `rangePrice()` doet dit NIET. Die zet tijdens het slepen
   *  `Number(value).toFixed(2)` in de preview, dus met een punt — de eerste paint komt uit
   *  Liquid met een komma en verspringt bij de eerste beweging naar een punt. Dat is een
   *  inconsistentie in het thema, niet iets om na te bouwen: hier blijft het altijd komma. */
  _money(euro) {
    const m = this.api.money || {};
    const dec = m.decimal || ',';
    const dui = m.thousands || '.';
    const vast = (Math.round(euro * 100) / 100).toFixed(2);
    const [heel, frac] = vast.split('.');
    return heel.replace(/\B(?=(\d{3})+(?!\d))/g, dui) + dec + frac;
  }

  /** Prijsfacet als schuifbalk — Eurus' markup uit input-filter.liquid regel 234-341.
   *
   *  WAAROM EEN SCHUIF EN GEEN TWEE VELDEN. De eerste versie had `Van`/`Tot`-nummervelden.
   *  Dat werkte, maar op elke andere collectie van deze winkel staat een schuif met een
   *  "Opnieuw instellen"-regel erboven en de twee bedragen eronder. Twee invoervelden maken
   *  van de grote collecties zichtbaar een andere pagina, en dat is precies wat dit hele
   *  systeem niet mag doen.
   *
   *  Elke klasse hieronder is letterlijk uit het snippet gekopieerd. Dat is niet uit luiheid:
   *  Eurus' CSS is voorgecompileerd, dus een klasse die het thema zelf niet gebruikt bestaat
   *  niet in het bundel en doet niets (dezelfde val die eerder de mobiele filterknop
   *  onzichtbaar maakte). Alleen `rounded-range` / `square-range` hebben echte CSS —
   *  theme.css.liquid regel 2608-2655, de duim van de schuif; de rest is Tailwind.
   *
   *  WEGGELATEN uit de originele markup: de twee verborgen `.price-input`-nummervelden
   *  (`opacity-0 -z-10`). Die bestaan alleen omdat Eurus het filterformulier naar Shopify
   *  submit; wij onderscheppen alles in JS en submitten nooit. Ze zijn onzichtbaar, dus dit
   *  kost geen enkel verschil in beeld — en een dood veld in de DOM is erger dan geen veld.
   *
   *  Grenzen: `f.max` komt uit de engine en is berekend met alle ándere filters wél
   *  toegepast maar prijs niet (query.js, all-but-X). Dat is exact wat Shopify's
   *  `filter.range_max` doet, dus de schuif krimpt mee met de rest van de selectie zonder
   *  zichzelf op te eten zodra je hem versleept. */
  _priceHTML(f, state) {
    const max = (f.max || 0) / 100;
    // Geen prijsspreiding om te tonen. Een schuif van 0 tot 0 is een dood element; de groep
    // helemaal weglaten is eerlijker dan een balk die niets doet.
    if (!(max > 0)) return '';

    const lo = state.filters.price_gte != null
      ? Math.min(max, Math.max(0, state.filters.price_gte / 100)) : 0;
    const hi = state.filters.price_lte != null
      ? Math.min(max, Math.max(0, state.filters.price_lte / 100)) : max;
    const actief = state.filters.price_gte != null || state.filters.price_lte != null;
    // Zelfde regel als bij de lijstfacetten: alleen open als de bezoeker hem opent.
    const open = this.openGroups.has('price')
      || (this.api.groupsStartCollapsed === false && actief);
    // De reset-knop staat er altijd en is altijd klikbaar, ook zonder actief prijsfilter —
    // net als bij Eurus. Hem `disabled` geven leek netter, maar een disabled button krijgt in
    // de meeste browsers een grijze systeemkleur en dan wijkt de kolom zichtbaar af.
    // input-filter.liquid:244 — alles behalve 'square' krijgt een ronde duim.
    const duim = this.api.filterEdges !== 'square' ? 'rounded-range' : 'square-range';
    const links = (lo / max) * 100;
    const rechts = 100 - (hi / max) * 100;
    const t = this.api.textSizes || {};
    const resetKlasse = t.resetDesktop
      ? ` text-[${t.resetMobile}rem] md:text-[${t.resetDesktop}rem]` : '';
    const reset = (this.api.strings || {}).reset || 'Opnieuw instellen';
    const sym = (this.api.money || {}).symbol || '€';

    return `
      <div class="${EURUS.group}" data-facet="price">
        <button type="button" class="${EURUS.summary}" data-dgm-toggle aria-expanded="${open}"
          aria-label="${esc(f.label)}">
          <span class="${EURUS.summaryLabel}">${esc(f.label)}</span>
          <span class="${EURUS.caret} ${open ? 'rotate-180' : 'rotate-0'}">${CARET_SVG}</span>
        </button>
        <div class="${EURUS.body}" data-dgm-body${open ? '' : ' hidden'}>
          <div class="pt-4 pb-4 justify-end flex${resetKlasse}">
            <button type="button" class="facet-reset cursor-pointer ml-5"
              style="color: rgb(var(--colors-text))"
              data-dgm-price-reset>${esc(reset)}</button>
          </div>
          <div class="flex pb-4 md:mb-4 items-center justify-between">
            <div class="w-full range-price-filter px-1">
              <div class="w-full text-[rgb(var(--colors-text),0.7)]">
                <div class="slider h-1.5 relative border border-solid">
                  <div style="left: ${links.toFixed(3)}%; right: ${rechts.toFixed(3)}%"
                    class="progress h-full absolute bg-[rgb(var(--colors-text-link))] left-0 right-0"
                    data-dgm-progress></div>
                </div>
                <div class="range-input relative">
                  <input type="range" data-dgm-range="min" aria-label="Minimumprijs"
                    min="0" max="${max}" step=".01" value="${lo}"
                    class="absolute -translate-x-1 rtl:translate-x-1 range-min w-full bg-none h-1.5 -top-1.5 pointer-events-none appearance-none ${duim}">
                  <input type="range" data-dgm-range="max" aria-label="Maximumprijs"
                    min="0" max="${max}" step=".01" value="${hi}"
                    class="absolute translate-x-1 rtl:-translate-x-1 range-max w-full bg-none h-1.5 -top-1.5 pointer-events-none appearance-none ${duim}">
                </div>
                <div class="w-full flex justify-between mt-3">
                  <div class="flex items-center">
                    <span>${esc(sym)}</span>
                    <p class="price-preview" data-dgm-price-preview="min">${this._money(lo)}</p>
                  </div>
                  <div class="flex items-center">
                    <span>${esc(sym)}</span>
                    <p class="price-preview" data-dgm-price-preview="max">${this._money(hi)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>`;
  }

  /** De schuif laten leven. Twee soorten gebeurtenissen, met opzet gescheiden:
   *
   *   * `input` (tijdens slepen) → alleen de balk en de twee bedragen bijwerken. Géén query,
   *     want `_commit` bouwt de hele kolom opnieuw op en dan verliest de bezoeker de duim
   *     die hij vasthoudt.
   *   * `change` (duim losgelaten) → pas dan filteren. Eurus doet het net zo: `@change` roept
   *     `onSubmit()` aan, `input` niet.
   *
   *  De minimale afstand tussen de duimen is 1 valuta-eenheid, zoals Eurus' `priceGap`. Zijn
   *  implementatie daarvan is wel overgeslagen: die zet in één tak `priceInput[1].value =
   *  minVal + priceGap` op een string, wat "01" oplevert in plaats van 1 hoger. Hier wordt
   *  simpelweg de duim geklemd die je niet vasthoudt. */
  _wirePrice(scope) {
    const herstel = scope.querySelector('[data-dgm-price-reset]');
    if (herstel) {
      herstel.addEventListener('click', (e) => {
        e.preventDefault();
        const st = this._mutableState();
        delete st.filters.price_gte;
        delete st.filters.price_lte;
        this._commit(st);
      });
    }

    const min = scope.querySelector('[data-dgm-range="min"]');
    const max = scope.querySelector('[data-dgm-range="max"]');
    if (!min || !max) return;

    const balk = scope.querySelector('[data-dgm-progress]');
    const vMin = scope.querySelector('[data-dgm-price-preview="min"]');
    const vMax = scope.querySelector('[data-dgm-price-preview="max"]');
    const grens = Number(max.max) || 0;
    const GAP = 1;

    const teken = (bron) => {
      let lo = Number(min.value);
      let hi = Number(max.value);
      if (hi - lo < GAP) {
        // Klem de duim die NIET versleept wordt, zodat de gesleepte duim de bezoeker volgt.
        if (bron === min) hi = Math.min(grens, lo + GAP);
        else lo = Math.max(0, hi - GAP);
        min.value = String(lo);
        max.value = String(hi);
      }
      if (balk && grens > 0) {
        balk.style.left = `${((lo / grens) * 100).toFixed(3)}%`;
        balk.style.right = `${(100 - (hi / grens) * 100).toFixed(3)}%`;
      }
      if (vMin) vMin.textContent = this._money(lo);
      if (vMax) vMax.textContent = this._money(hi);
      return { lo, hi };
    };

    const leggenVast = (bron) => {
      const { lo, hi } = teken(bron);
      const st = this._mutableState();
      // Een duim op het uiterste betekent "geen grens", niet "grens op de rand". Anders
      // blijft er een prijsfilter in de URL en op de chip staan terwijl de schuif volledig
      // open staat, en dan lijkt de reset-knop stuk.
      if (lo <= 0) delete st.filters.price_gte;
      else st.filters.price_gte = Math.round(lo * 100);
      if (hi >= grens) delete st.filters.price_lte;
      else st.filters.price_lte = Math.round(hi * 100);
      this._commit(st);
    };

    for (const inp of [min, max]) {
      inp.addEventListener('input', () => teken(inp));
      inp.addEventListener('change', () => leggenVast(inp));
    }
  }

  _wireGroups(scope, res) {
    for (const group of scope.querySelectorAll('[data-facet]')) {
      const key = group.dataset.facet;
      const knop = group.querySelector('[data-dgm-toggle]');
      const body = group.querySelector('[data-dgm-body]');
      const caret = knop && knop.querySelector('span:last-child');

      if (knop && body) {
        knop.addEventListener('click', (e) => {
          e.preventDefault();
          const wordtOpen = !this.openGroups.has(key);
          if (wordtOpen) this.openGroups.add(key); else this.openGroups.delete(key);

          // Inhoud pas opbouwen bij eerste opening. De index staat al in de browser, dus
          // dit kost geen netwerk; 231 checkboxen maal 7 facetten direct in de DOM zetten
          // is wel een meetbare kost.
          if (wordtOpen && body.innerHTML.trim() === '') {
            const f = (this.lastResult || res).facets.find((x) => x.key === key);
            if (f) { body.innerHTML = this._valuesHTML(f); this._wireValues(body, key); }
          }

          // Zelf zichtbaar maken. Dit ontbrak: de inhoud werd wel gevuld maar bleef
          // verborgen, want de body was met `hidden` (eerder `x-cloak`) dichtgezet en
          // niemand haalde dat weg.
          body.hidden = !wordtOpen;
          knop.setAttribute('aria-expanded', String(wordtOpen));
          if (caret) {
            caret.classList.toggle('rotate-180', wordtOpen);
            caret.classList.toggle('rotate-0', !wordtOpen);
          }
        });
      }
      if (body && body.innerHTML.trim() !== '') this._wireValues(body, key);
    }
  }

  _wireValues(scope, key) {
    for (const cb of scope.querySelectorAll('input[type="checkbox"][data-facet]')) {
      cb.addEventListener('change', () => {
        const st = this._mutableState();
        zetFacetWaarde(st.filters, cb.dataset.facet, cb.value, cb.checked);
        this._commit(st);
      });
    }

    const zoek = scope.querySelector('[data-facet-search]');
    if (zoek) {
      let t;
      zoek.addEventListener('input', () => {
        clearTimeout(t);
        t = setTimeout(() => {
          this.searchTerms.set(key, zoek.value);
          const f = this.lastResult.facets.find((x) => x.key === key);
          const body = zoek.closest('[data-dgm-body]');
          if (!f || !body) return;
          const pos = zoek.selectionStart;
          body.innerHTML = this._valuesHTML(f);
          this._wireValues(body, key);
          const opnieuw = body.querySelector('[data-facet-search]');
          if (opnieuw) { opnieuw.focus(); opnieuw.setSelectionRange(pos, pos); }
        }, DEBOUNCE_MS);
      });
    }

    const meer = scope.querySelector('[data-facet-more]');
    if (meer) {
      meer.addEventListener('click', () => {
        this.showAll.add(key);
        const f = this.lastResult.facets.find((x) => x.key === key);
        const body = meer.closest('[data-dgm-body]');
        if (f && body) { body.innerHTML = this._valuesHTML(f); this._wireValues(body, key); }
      });
    }

    // Het prijsfacet heeft geen checkboxen maar een schuif; zijn markup staat direct in
    // _priceHTML en is dus altijd aanwezig zodra de groep gerenderd is.
    if (scope.querySelector('[data-dgm-range]') || scope.querySelector('[data-dgm-price-reset]')) {
      this._wirePrice(scope);
    }
  }

  /* --- staat en grid ---------------------------------------------------- */

  _mutableState() {
    const src = this.state;
    return { filters: { ...src.filters }, sortBy: src.sortBy, page: 1 };
  }

  /** Direct toepassen, ook op mobiel. Eerder batchte deze laag mobiel tot een eigen
   *  'resultaten tonen'-knop, maar Eurus heeft die knop niet en past elke tik direct toe.
   *  Een eigen bevestigstap zou dus een verschil met een reguliere collectie zijn. */
  _commit(next) {
    this.state = next;
    this.render();
    this._swapGrid();
  }

  async _swapGrid({ pushState = true } = {}) {
    const res = this.lastResult || this.render();
    if (pushState) {
      history.pushState({ dgm: true }, '', this.api.urlFromState(this.state));
    }
    if (!res.handles.length) {
      this.grid.innerHTML =
        '<p class="col-span-full py-8">Geen producten gevonden met deze filters. '
        + '<button type="button" class="dgm-facet-more" data-dgm-clear-inline>Filters wissen</button></p>';
      const btn = this.grid.querySelector('[data-dgm-clear-inline]');
      if (btn) {
        btn.addEventListener('click', () => {
          this._commit({ filters: {}, sortBy: this.state.sortBy, page: 1 }, { force: true });
        });
      }
      return;
    }
    try {
      const { html } = await this.api.fetchCards(res.handles);
      // Niet-destructief: eerst ophalen, dan verwisselen. Mislukt het ophalen, dan blijft
      // het oude grid staan in plaats van dat de pagina leeg raakt.
      this.grid.innerHTML = html;
    } catch (e) {
      console.warn('[dgm-facets] kaarten ophalen mislukt, oude grid blijft staan:', e.message);
    }
  }
}

/* Aanhaken zodra dgm-facets-ui.js de index geladen heeft. Faalt dat, dan komt dit event
 * nooit en blijft de native pagina intact. */
document.addEventListener('dgm:facets:ready', (e) => {
  try {
    new FacetsRenderer(e.detail).mount();
  } catch (err) {
    console.warn('[dgm-facets] renderen mislukt, native pagina blijft staan:', err);
  }
});
